/**
 * CreateTreatmentTypeModal — migrated to unified modal system.
 * 5 fields: code, name, nameEn, description, color (hex picker).
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileTextIcon, HashIcon, PaletteIcon, StickyNoteIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, FormField, useVinylSubmit, MODAL_COLORS } from '@/components/modal';
import { useCreateTreatmentType } from '@/features/reports/hooks';
import type { TreatmentTypeFormData } from '@/features/reports/types';

interface CreateTreatmentTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateTreatmentTypeModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateTreatmentTypeModalProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const createTreatmentType = useCreateTreatmentType();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isAnimating, submitWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: 'TREATMENT TYPE',
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#004a99');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setName('');
      setNameEn('');
      setDescription('');
      setColor('#004a99');
      setError('');
    }
  }, [isOpen]);

  const focusField = (id: string) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
    });
  };

  const validateForm = (): boolean => {
    setError('');
    if (!code.trim()) {
      setError(isHebrew ? '\u05E0\u05D0 \u05DC\u05D4\u05D6\u05D9\u05DF \u05E7\u05D5\u05D3' : 'Code is required');
      focusField('ctt-code');
      return false;
    }
    if (!name.trim()) {
      setError(isHebrew ? '\u05E0\u05D0 \u05DC\u05D4\u05D6\u05D9\u05DF \u05E9\u05DD' : 'Name is required');
      focusField('ctt-name');
      return false;
    }
    return true;
  };

  const isFormValid = useMemo(() => {
    return !!(code.trim() && name.trim());
  }, [code, name]);

  const handleSubmit = () => {
    const payload: TreatmentTypeFormData = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      nameEn: nameEn.trim() || undefined,
      description: description.trim() || undefined,
      color: color.trim() || undefined,
    };

    submitWithAnimation({
      validate: validateForm,
      mutate: () => createTreatmentType.mutateAsync(payload),
      onSuccess: () => {
        toast.success(t('treatmentsAdmin.createSuccess'));
        onSuccess();
        onClose();
      },
      onError: (err: any) => {
        const msg = err.response?.data?.message || '';
        if (msg.includes('already exists')) {
          setError(t('treatmentsAdmin.codeExists'));
        } else {
          setError(err.response?.data?.error || msg || t('treatmentsAdmin.createFailed'));
        }
      },
    });
  };

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('treatmentsAdmin.createTitle')}
      isPending={createTreatmentType.isPending}
      isAnimating={isAnimating}
      onSubmit={handleSubmit}
      onValidate={validateForm}
      isFormValid={isFormValid}
      modalContentRef={modalContentRef}
      vinylLabel="TREATMENT TYPE"
      error={error}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<HashIcon className="h-4 w-4" />} label={t('treatmentsAdmin.code')} required>
            <input
              id="ctt-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="OCD"
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<FileTextIcon className="h-4 w-4" />} label={t('treatmentsAdmin.name')} required>
            <input
              id="ctt-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Obsessive-Compulsive Disorder"
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>
        <FormField icon={<FileTextIcon className="h-4 w-4" />} label={t('treatmentsAdmin.nameEn')}>
          <input
            id="ctt-nameEn"
            type="text"
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            placeholder="e.g. Obsessive-Compulsive Disorder"
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>
        <FormField icon={<StickyNoteIcon className="h-4 w-4" />} label={t('treatmentsAdmin.description')}>
          <textarea
            id="ctt-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' as const }}
          />
        </FormField>
        <FormField icon={<PaletteIcon className="h-4 w-4" />} label={t('treatmentsAdmin.color')}>
          <div className="flex items-center gap-2">
            <input
              id="ctt-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-12 h-10 rounded-lg cursor-pointer border border-gray-200"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="#004a99"
              maxLength={7}
              className="flex-1 p-2.5 rounded-lg font-mono font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </div>
        </FormField>
      </div>
    </FormModal>
  );
}
